import React, { useCallback, useEffect, useState } from 'react';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { fetchSettingsPortal, atualizarSettingPortal } from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Seção "E-mails ao fornecedor" da configuração de Cotações (at-11, 28/09/2026 — pedido do
 * Victor).
 *
 * São os DOIS e-mails que o comprador dispara depois de a cesta fechar: o **espelho da nota
 * fiscal** (antes de o pedido entrar no ERP, para conferir itens e valores) e a **autorização
 * de faturamento** (depois que o pedido entrou).
 *
 * ⚠️ Não é o texto do CONVITE — aquele fica na tela de Integrações e tem `{{link}}`, que aqui
 * não existe: não há nada para o fornecedor abrir, a resposta esperada vem por e-mail.
 *
 * Estilo: nada de CSS próprio — card, hint, input e botão vêm de `adminSistema.css`, como as
 * abas vizinhas (molde do `FormasPagamentoSection` e do `AlertasEtapaSection`).
 */

interface TextoConfiguravel {
  chaveAssunto: string;
  chaveCorpo: string;
  titulo: string;
  quando: string;
  placeholders: string;
}

const TEXTOS: TextoConfiguravel[] = [
  {
    chaveAssunto: 'EMAIL_ESPELHO_NF_ASSUNTO',
    chaveCorpo: 'EMAIL_ESPELHO_NF_CORPO',
    titulo: 'Solicitação do espelho da nota fiscal',
    quando: 'Disparado depois de fechar e aprovar a cesta, antes de o pedido entrar no ERP.',
    placeholders: '{{fornecedor}}, {{numero}}, {{comprador}}, {{itens}}, {{total}}',
  },
  {
    chaveAssunto: 'EMAIL_AUTORIZACAO_FATURAMENTO_ASSUNTO',
    chaveCorpo: 'EMAIL_AUTORIZACAO_FATURAMENTO_CORPO',
    titulo: 'Autorização de faturamento',
    quando: 'Disparado na linha do pedido, depois que ele entrou no ERP.',
    placeholders:
      '{{fornecedor}}, {{numero}}, {{comprador}}, {{itens}}, {{total}}, {{pedido}}, {{previsao}}',
  },
  {
    // portal-cotacoes/at-16 (reunião de 28/10/2026): o comprador manda ao fornecedor o que ele respondeu.
    chaveAssunto: 'EMAIL_RESUMO_PROPOSTA_ASSUNTO',
    chaveCorpo: 'EMAIL_RESUMO_PROPOSTA_CORPO',
    titulo: 'Resumo da proposta',
    quando: 'Disparado pelo comprador, na linha de cada fornecedor que respondeu, enquanto a cotação estiver aberta.',
    placeholders: '{{fornecedor}}, {{numero}}, {{comprador}}, {{itens}}, {{frete}}, {{condicao}}, {{versao}}',
  },
];

const CHAVES = TEXTOS.flatMap((t) => [t.chaveAssunto, t.chaveCorpo]);

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Os e-mails',
    conteudo: (
      <p>
        Depois de fechar a cesta, o comprador pede ao fornecedor o <strong>espelho da nota fiscal</strong> — a
        prévia com itens e valores, para conferir antes de comprar. Depois que o pedido entra no ERP, ele{' '}
        <strong>autoriza o faturamento</strong>. E, a qualquer momento depois de o fornecedor responder, dá para
        mandar a ele o <strong>resumo da proposta</strong>, com tudo o que ele informou, para conferir. Todos saem
        do detalhe da cotação, um clique por fornecedor. No resumo, <strong>{'{{itens}}'}</strong> traz cada
        oferta com a condição, o preço e o prazo.
      </p>
    ),
  },
  {
    titulo: 'O que cada marcação vira',
    conteudo: (
      <div>
        <p>
          O texto é enviado como está, trocando as marcações entre chaves pelos valores daquele envio:
        </p>
        <p>
          <strong>{'{{fornecedor}}'}</strong> a razão social. <strong>{'{{numero}}'}</strong> o número da
          cotação, que é o da requisição. <strong>{'{{comprador}}'}</strong> quem conduz a cotação.{' '}
          <strong>{'{{itens}}'}</strong> a lista, uma linha por item, <strong>só dos itens que aquele
          fornecedor venceu</strong>. <strong>{'{{total}}'}</strong> a soma desses itens.{' '}
          <strong>{'{{pedido}}'}</strong> e <strong>{'{{previsao}}'}</strong> existem apenas na autorização de
          faturamento.
        </p>
        <p>
          Marcação escrita errada <strong>aparece literal no e-mail</strong>, entre chaves — é assim que o erro
          fica visível, em vez de a frase sair truncada sem ninguém notar.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Não existe link nestes e-mails',
    conteudo: (
      <p>
        Ao contrário do convite, aqui não há <strong>{'{{link}}'}</strong>: não existe nada para o fornecedor
        abrir. A cotação já foi decidida, e o que se espera dele é uma resposta por e-mail. Um link levaria de
        volta ao formulário de proposta de uma cotação encerrada.
      </p>
    ),
  },
  {
    titulo: 'Editar vale para os próximos envios',
    conteudo: (
      <p>
        O que já foi enviado é <strong>registro</strong> e não muda. O detalhe da cotação mostra, por
        fornecedor, a data do último envio, quem enviou, para qual e-mail e se deu certo — o corpo da mensagem
        não é guardado.
      </p>
    ),
  },
];

interface EmailsFornecedorSectionProps {
  /** Só ADMIN/TI gravam configuração no Portal (`PUT /settings/:key`) — ADR 0012. */
  podeEditar: boolean;
}

const EmailsFornecedorSection: React.FC<EmailsFornecedorSectionProps> = ({ podeEditar }) => {
  const [valores, setValores] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const settings = await fetchSettingsPortal();
      setValores(Object.fromEntries(CHAVES.map((c) => [c, settings[c] ?? ''])));
      setErro(false);
    } catch (error) {
      /*
       * Campos vazios por erro seriam lidos como "os textos não foram configurados", e salvar
       * por cima **apagaria** os que estão valendo (regra da at-60). Por isso o formulário não
       * é montado quando a carga falha.
       */
      setValores({});
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os textos dos e-mails. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar(texto: TextoConfiguravel) {
    const assunto = (valores[texto.chaveAssunto] ?? '').trim();
    const corpo = (valores[texto.chaveCorpo] ?? '').trim();
    if (!assunto || !corpo) {
      notify.warning('Preencha o assunto e o texto antes de salvar — e-mail sem um dos dois não sai.');
      return;
    }

    setSalvando(texto.chaveCorpo);
    try {
      // Em série, e não em paralelo: com `Promise.all`, uma falha no segundo deixaria o
      // assunto novo com o corpo antigo, e a tela não saberia dizer qual dos dois valeu.
      await atualizarSettingPortal(texto.chaveAssunto, assunto);
      await atualizarSettingPortal(texto.chaveCorpo, corpo);
      notify.success(`Texto de "${texto.titulo}" salvo com sucesso.`);
    } catch (error) {
      notify.errorFrom(error, `Não foi possível salvar o texto de "${texto.titulo}".`);
      // Recarrega para a tela voltar a mostrar o que está VALENDO: com o salvamento parcial, o
      // que está na caixa pode não ser o que o fornecedor vai receber.
      carregar();
    } finally {
      setSalvando(null);
    }
  }

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-title">
          E-mails ao fornecedor
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setAjudaAberta(true)}
          >
            ?
          </button>
        </div>

        <p className="admin-hint" style={{ marginTop: 0 }}>
          {podeEditar
            ? 'Os textos dos dois e-mails que o comprador dispara no detalhe da cotação depois de fechar a cesta. Editar vale para os próximos envios.'
            : 'Os textos dos dois e-mails que você dispara no detalhe da cotação. São mantidos por um administrador ou pela TI.'}
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar os textos, então não dá para saber o que está valendo hoje. Recarregue a
            página antes de editar — salvar agora substituiria os textos em vigor por caixas vazias.
          </p>
        ) : carregando ? (
          <p className="admin-hint">Carregando...</p>
        ) : (
          TEXTOS.map((texto) => (
            <div key={texto.chaveCorpo} style={{ marginBottom: 24 }}>
              <div className="admin-label" style={{ fontWeight: 600 }}>
                {texto.titulo}
              </div>
              <p className="admin-hint" style={{ margin: '2px 0 8px' }}>
                {texto.quando} Marcações aceitas: {texto.placeholders}.
              </p>

              <div className="admin-input-group" style={{ marginBottom: 8 }}>
                <label className="admin-label" htmlFor={texto.chaveAssunto}>
                  Assunto
                </label>
                <input
                  id={texto.chaveAssunto}
                  className="admin-input"
                  type="text"
                  disabled={!podeEditar || salvando !== null}
                  value={valores[texto.chaveAssunto] ?? ''}
                  onChange={(e) =>
                    setValores((atual) => ({ ...atual, [texto.chaveAssunto]: e.target.value }))
                  }
                />
              </div>

              <div className="admin-input-group" style={{ marginBottom: 8 }}>
                <label className="admin-label" htmlFor={texto.chaveCorpo}>
                  Texto
                </label>
                <textarea
                  id={texto.chaveCorpo}
                  className="admin-input"
                  rows={8}
                  disabled={!podeEditar || salvando !== null}
                  value={valores[texto.chaveCorpo] ?? ''}
                  onChange={(e) => setValores((atual) => ({ ...atual, [texto.chaveCorpo]: e.target.value }))}
                />
              </div>

              {podeEditar && (
                <button
                  type="button"
                  className="admin-btn"
                  onClick={() => salvar(texto)}
                  disabled={salvando !== null}
                >
                  {salvando === texto.chaveCorpo ? 'Salvando...' : 'Salvar este texto'}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - E-mails ao fornecedor"
        secoes={helpSections}
      />
    </>
  );
};

export default EmailsFornecedorSection;
